import React, { lazy, Suspense, useEffect, useMemo } from 'react';
import { Outlet, RouteObject, useMatches } from 'react-router-dom';
import { NavLinkId } from '../../data/types/nav';
import { NavConfig } from '../../data/constants/nav';
import { ErrorBoundary } from 'react-error-boundary';
import ebisPrintRoutes from './ebisPrintRoutes';
import rosterRoutes from './rosterRoutes';
import mjcRoutes from './mjcRoutes';
import mncRoutes from './mncRoutes';
import intlRoutes from './intlRoutes';
import txRoutes from './txRoutes';
import utilRoutes from './utilRoutes';
import systemRoutes from './systemRoutes';
import empRoutes from './empRoutes';
import cepRoutes from './cepRoutes';
import profileRoutes from './profileRoutes';
import r4Routes from './r4Routes';
import reportRoutes from './reportRoutes';
import r5Routes from './r5Routes';
import loginRoutes from './loginRoutes';

const GlobalProviders = lazy(() => import('../GlobalProviders'));
const EbisAppFrame = lazy(() => import('../frames/EbisAppFrame'));
const AdvSearch = lazy(() => import('../adv-search/AdvSearch'));
const ClipboardMain = lazy(() => import('../clipboard/ClipboardMain'));
const Clipboard = lazy(() => import('../clipboard/Clipboard'));
const AwardBonusPackage = lazy(
  () => import('../major-contracts/MjAwardBonusPackages/AwardBonusPackage')
);
const AffiliateProfile = lazy(
  () => import('../utilities/affiliates/AffiliateProfile')
);

const DocumentTitleUpdater = (): null => {
  const matches = useMatches();

  const title = useMemo(() => {
    const match = matches
      .reverse()
      .find(({ id }) => !!NavConfig.linkConfigs[id as NavLinkId]?.label);
    return NavConfig.linkConfigs[match?.id as NavLinkId]?.label ?? 'Ebis';
  }, [matches]);

  useEffect(() => {
    document.title = title;
  }, [title]);

  return null;
};

const routes: RouteObject[] = [
  ...loginRoutes,
  {
    element: (
      <Suspense>
        <GlobalProviders>
          <Suspense>
            <Outlet />
          </Suspense>
        </GlobalProviders>
      </Suspense>
    ),
    children: [
      ...ebisPrintRoutes,
      {
        element: (
          <>
            <DocumentTitleUpdater />
            <EbisAppFrame>
              <ErrorBoundary fallback={<div />}>
                <Suspense>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            </EbisAppFrame>
          </>
        ),
        children: [
          ...rosterRoutes,
          ...mjcRoutes,
          {
            id: NavConfig.linkConfigs.AWARD_BONUS_PACKAGE.id,
            path: NavConfig.linkConfigs.AWARD_BONUS_PACKAGE.to,
            element: <AwardBonusPackage />,
          },
          ...mncRoutes,
          ...intlRoutes,
          ...txRoutes,
          ...utilRoutes,
          ...systemRoutes,
          ...empRoutes,
          {
            id: NavConfig.linkConfigs.ADVANCED_SEARCH.id,
            path: NavConfig.linkConfigs.ADVANCED_SEARCH.to,
            element: <AdvSearch />,
          },
          {
            id: NavConfig.linkConfigs.CLIPBOARD.id,
            path: NavConfig.linkConfigs.CLIPBOARD.to,
            element: (
              <ClipboardMain>
                <Suspense>
                  <Outlet />
                </Suspense>
              </ClipboardMain>
            ),
            children: [
              {
                index: true,
                element: <Clipboard />,
              },
            ],
          },
          ...cepRoutes,
          {
            id: NavConfig.linkConfigs.AFFILIATE_PROFILE.id,
            path: NavConfig.linkConfigs.AFFILIATE_PROFILE.to,
            element: <AffiliateProfile />,
          },
          ...profileRoutes,
          ...r4Routes,
          ...reportRoutes,
          ...r5Routes,
        ],
      },
    ],
  },
];

export default routes;
